import React, { useEffect, useState } from "react";
import { Input, Button, Modal, Select, Radio, Alert, Tag } from "antd";

import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import { useForm, Controller } from "react-hook-form";
import axios from "axios";
import ButtonLoader from "../ButtonLoader";
import LocationCheckboxes from "../Locations/LocationCheckboxes";

const { Option } = Select;

const schema = yup.object().shape({
  first_name: yup.string().required("First name is required"),
  last_name: yup.string().required("Last name is required"),
  role: yup.string().required("Role is required"),
  status: yup.string().required("Status is required"),
});

const UserForm = ({
  isModalOpen,
  editingUser,
  closeModal,
  handleUpdateList,
}) => {
  const [loading, setLoading] = useState(true);
  const [showErrors, setShowErrors] = useState(false);
  const [errorsMessage, setErrorsMessage] = useState("");
  const [userData, setUserData] = useState(null);
  const [roleOptions, setRoleOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [isDataSet, setIsDataSet] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    context: { isEdit: !!editingUser },
    defaultValues: {
      status: "active",
    },
  });

  const fetchMasterData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/users/masters`
      );

      const status = response.data.data.status;
      const roles = response.data.data.roles;
      const locations = response.data.data.locations;

      setStatusOptions(status);
      setRoleOptions(roles);
      setLocationOptions(locations);
      setLoading(false);
    } catch (err) {
      console.error(err.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchDataAndSetState = async () => {
      await fetchMasterData();
      setIsDataSet(true);
    };
    fetchDataAndSetState();
  }, []);

  const fetchUser = async (userId) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/users/${userId}`
      );
      const tempRole = roleOptions.find((role) => {
        if (response.data.data.role_id) {
          return role.id === response.data.data.role_id;
        } else {
          return role.is_default === 1;
        }
      });
      const editFormData = {
        ...response.data.data,
        status: response.data.data.activated === 1 ? "active" : "inactive",
        role: tempRole.id,
      };
      setSelectedRole(tempRole);
      setSelectedLocations(editFormData.user_locations);
      setUserData(editFormData);
      reset(editFormData);
      setLoading(false);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    if (isDataSet && editingUser) {
      fetchUser(editingUser.id);
    }
  }, [isDataSet, editingUser]);

  const onSubmitUser = async (data) => {
    let differFormFields = {};
    if (editingUser !== null) {
      differFormFields = {
        id: editingUser.id,
      };
    }
    const finalFormData = {
      ...differFormFields,
      role_id: data.role,
      status: data.status,
      first_name: data.first_name,
      last_name: data.last_name,
      locations: selectedLocations,
    };
    setSubmitting(true);
    let newFormData = finalFormData;
    const url =
      editingUser === null
        ? `${process.env.REACT_APP_BASE_URL}/api/users/create`
        : `${process.env.REACT_APP_BASE_URL}/api/users/update`;

    try {
      if (editingUser === null) {
        await axios.post(url, newFormData);
        // Reset form after successful submission
        reset(newFormData);
      } else {
        const response = await axios.put(url, newFormData);
        const updated_row_count = response.data.data.updated_row_count;
        if (updated_row_count === 1) {
          reset(data);
        }
      }
      setSubmitting(false);
      setShowErrors(false);
      handleUpdateList();
      closeModal();
    } catch (error) {
      if (error.response && error.response.status > 300) {
        const errorData = error.response.data.message;
        // const errorField = error.response.data.field;
        console.log(errorData);
        // setShowErrors(false);
        setErrorsMessage(errorData);
        setShowErrors(true);
        setTimeout(() => {
          setShowErrors(false);
        }, 3000);

        setSubmitting(false);
      } else {
        console.error("Error:", error);
      }
    }
  };

  return (
    <Modal
      title={editingUser ? "Edit User" : "Add New User"}
      open={isModalOpen}
      onCancel={() => {
        reset();
        // setEditingUser(null);
        closeModal();
      }}
      width={{
        xs: "90%",
        sm: "80%",
        md: "70%",
        lg: "70%",
        xl: "50%",
        xxl: "40%",
      }}
      footer={null}
      maskClosable={false}
    >
      {loading ? (
        <ButtonLoader wrapperClass={`min-h-50`} />
      ) : (
        <>
          {showErrors && (
            <>
              <div className="col-sm-12 mb-3">
                <Alert message={errorsMessage} type="error" closeIcon={false} />
              </div>
            </>
          )}
          <form onSubmit={handleSubmit(onSubmitUser)}>
            <div className="mb-3">
              <label className="form-label">
                First Name <span className="text-danger">*</span>
              </label>
              <Controller
                name="first_name"
                control={control}
                render={({ field }) => <Input {...field} />}
              />
              {errors.first_name && (
                <div className="text-danger small">
                  {errors.first_name.message}
                </div>
              )}
            </div>
            <div className="mb-3">
              <label className="form-label">
                Last Name <span className="text-danger">*</span>
              </label>
              <Controller
                name="last_name"
                control={control}
                render={({ field }) => <Input {...field} />}
              />
              {errors.last_name && (
                <div className="text-danger small">
                  {errors.last_name.message}
                </div>
              )}
            </div>
            <div className="mb-3">
              <label className="form-label">
                Role <span className="text-danger">*</span>
              </label>
              <Controller
                name="role"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    style={{ width: "100%" }}
                    onChange={(e) => {
                      setSelectedRole(
                        roleOptions.find((role) => {
                          return role.id === e;
                        })
                      );
                      setValue("role", e, { shouldDirty: true });
                    }}
                    value={selectedRole && selectedRole.id}
                    allowClear
                  >
                    {roleOptions.map((role) => (
                      <Option value={role.id} key={role.id}>
                        {role.name}
                      </Option>
                    ))}
                  </Select>
                )}
              />
              {errors.role && (
                <div className="text-danger small">{errors.role.message}</div>
              )}
            </div>
            <div className="mb-1">
              <label className="form-label">
                Username: {userData && `${userData.username}`}
              </label>
            </div>
            <div className="mb-1">
              <label className="form-label">
                Email: {userData && `${userData.email}`}
              </label>
            </div>
            <div className="mb-3">
              <label className="form-label me-3">Status</label>
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <Radio.Group {...field}>
                    {statusOptions.map((status) => (
                      <Radio value={status.value} key={status.value}>
                        {status.label}
                      </Radio>
                    ))}
                  </Radio.Group>
                )}
              />
              {errors.status && (
                <div className="text-danger small">{errors.status.message}</div>
              )}
            </div>
            <div className="mb-1">
              <label className="form-label fs-5">Locations</label>
              <LocationCheckboxes
                userLocations={selectedLocations}
                masterLocations={locationOptions}
                updateSelectedLocations={(locations) => {
                  setSelectedLocations(locations);
                }}
              />
            </div>

            <Button
              type="primary"
              htmlType="submit"
              block
              disabled={submitting}
            >
              {!submitting ? (
                editingUser ? (
                  "Update User"
                ) : (
                  "Add User"
                )
              ) : (
                <ButtonLoader text="Submitting..." />
              )}
            </Button>
          </form>
        </>
      )}
    </Modal>
  );
};

export default UserForm;
